import styled, { css } from 'styled-components';
import { theme } from './theme';
import { flexCenter, flexColumnCenter, boxShadow } from './mixins';

// 共通コンテナ
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.space.xl} ${theme.space.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.space.md};
  }
`;

// ヘッダー関連
export const HeaderContainer = styled.header`
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.space.xs} 0;
  position: sticky;
  top: 0;
  z-index: 100;
  cursor: pointer;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.space.md} 0 calc(${theme.space.md} - 3vw);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    align-items: flex-start;
    padding: 0 ${theme.space.md} 0 calc(${theme.space.md} + 3vw);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.space.md} 0 calc(${theme.space.md} + 10vw);
  }
`;

export const Logo = styled.div`
  margin-bottom: 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: left;
  }
  
  a {
    display: block;
    transition: ${theme.transitions.default};
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

export const LogoImage = styled.img`
  height: 50px;
  width: auto;
  margin-top: 12px;
`;

// フッター関連
export const FooterContainer = styled.footer`
  background-color: ${theme.colors.text};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.space.md};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// ローディング関連
export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
`;

export const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// セクションタイトル関連
export const TitleWithSubtitle = styled.div`
  text-align: center;
  margin-bottom: ${theme.space.xl};
  padding-top: ${theme.space.xl};
`;

export const JapaneseTitle = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  margin-bottom: ${theme.space.xs};
`;

export const EnglishTitle = styled.div`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  margin-top: 0;
  letter-spacing: 2px;
`;

// ソーシャルリンク関連
export const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.space.xl};
  margin-top: ${theme.space.md};
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.space.lg};
    margin-top: ${theme.space.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.space.md};
    flex-wrap: wrap;
    margin-top: ${theme.space.xs};
  }
`;

export const SocialLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme.colors.text};
  transition: ${theme.transitions.default};
  width: 120px;
  
  &:hover {
    color: ${theme.colors.primary};
    transform: translateY(-2px);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 90px;
  }
`;

export const SocialIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${theme.radii.full};
  background-color: #fff;
  transition: ${theme.transitions.default};
  
  ${SocialLink}:hover & {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
  
  svg {
    font-size: 60px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 70px;
    height: 70px;
    
    svg {
      font-size: 50px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
    
    svg {
      font-size: 40px;
    }
  }
`;

// ナビゲーションボタン関連
export const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  color: ${theme.colors.primary};
  font-weight: bold;
  font-size: 1.2rem;

  &:hover {
    background-color: ${theme.colors.primary};
    color: white;
  }

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }
  
  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
    font-size: 1rem;
    
    &.prev {
      left: 10px;
    }
    
    &.next {
      right: 10px;
    }
  }
`;

// カード関連
export const CardCategory = styled.span`
  background-color: rgba(29, 157, 231, 0.4);
  color: ${theme.colors.white};
  padding: 2px ${theme.space.xs};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.xs};
  display: inline-block;
  letter-spacing: 0.3px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1px 3px;
    font-size: 9px;
    line-height: 1.2;
    height: 16px;
    display: flex;
    align-items: center;
  }
`;

export const CardSubCategory = styled(CardCategory)`
  background-color: rgba(52, 194, 71, 0.4);
  color: ${theme.colors.white};
`;

export const CardTag = styled.span`
  background-color: rgba(255, 99, 71, 0.6);
  color: ${theme.colors.white};
  padding: 2px ${theme.space.xs};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.3px;
  margin-left: 2px;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1px 3px;
    margin-left: 1px;
    margin-bottom: 2px;
    font-size: 9px;
    line-height: 1.2;
    height: 16px;
    display: flex;
    align-items: center;
  }
`; 